import { useMemo } from 'react'
import { css } from '@emotion/react'
import {
  DesignToken,
  ProgressBar,
} from '@livechat/design-system-react-components'

import mediaQueries from '@/layout/media-queries'
import { type Histogram } from '@/interfaces/feedback'

const votesDistributionWrapperCss = css`
  display: flex;
  flex-direction: column;
  width: 120px;
  margin: 15px 20px 15px 0;

  ${mediaQueries.xsmallScreen(-30)} {
    transform: scale(0.95);
    margin-top: 7px;
  }

  ${mediaQueries.smallScreen(-100)} {
    margin: 7px 20px 0 60px;
  }
`

const rowCss = (fullWidth: boolean, color: string) =>
  css`
    display: flex;
    justify-content: center;
    align-items: center;
    width: ${fullWidth ? '100%' : '120px'};
    height: 10px;

    .progress-bar > div {
      background-color: ${color || `var(${DesignToken.ContentBasicSecondary})`};
    }
  `

const textCss = css`
  margin: 0;
  width: 8px;
  font-size: 8px;
  color: var(${DesignToken.ContentBasicSecondary});
  flex-shrink: 0;
  text-align: center;
  font-weight: bold;
`

const starCss = css`
  margin: 0;
  width: 10px;
  font-size: 6px;
  color: var(${DesignToken.ContentBasicSecondary});
  flex-shrink: 0;
`

const normalizeHistogramRate = (key: string) => {
  if (key?.length > 1) {
    return key.charAt(key.length - 1)
  }

  return key
}

const normalizeHistogram = (data: Histogram = {}) => {
  return Object.entries(data)
    .map(([key, value]) => ({
      rate: normalizeHistogramRate(key),
      percent: value?.percent || 0,
      votes: value?.votes || 0,
    }))
    .reverse()
}

interface Props {
  histogram: Histogram
  fullWidth: boolean
  color: string
}

const VotesDistribution = ({ histogram, fullWidth, color }: Props) => {
  const data = useMemo(() => normalizeHistogram(histogram), [histogram])

  return (
    <div css={votesDistributionWrapperCss}>
      {data.map(({ rate, percent }) => (
        <div css={rowCss(fullWidth, color)} key={`rates-histogram-${rate}`}>
          <p css={textCss}>{rate}</p>
          <p css={starCss}>★</p>
          <ProgressBar
            percent={percent}
            size="small"
            className="progress-bar"
          />
        </div>
      ))}
    </div>
  )
}

export default VotesDistribution
